import ko from "knockout";
import _ from "lodash";
import Promise from "bluebird";
import $ from "jquery";
import moment from "moment";
import { DataSet } from "vis-data";
import ViewModel from "viewModels/ViewModel";
import conversationTimelineUtil from "viewModels/util/ConversationTimelineUtil";
import externalContactService from "services/externalContactService";
import userService from "services/userService";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";
import LOG from "services/loggingService";
import errorCallbackFactory from "util/errorCallbackFactory";
import {
    FULL_DATE_MEDIUM_TIME_OPTIONS,
    DateTimeHelper,
} from "util/DateTimeHelper";

const MONITORING_SEGMENT_TYPE_OVERRIDES = {
    message: "monitoring.message",
    voice: "monitoring.voice",
};

/**
 * Helper function to check if timeline group data has changed
 * Prevents unnecessary DOM re-renders
 *
 * @param {Object} existing - Current group object from DataSet
 * @param {Object} incoming - New group object to potentially update
 * @returns {boolean} True if data has changed and update is needed
 */
const hasGroupChanged = function (existing, incoming) {
    if (!existing) return true;

    return (
        existing.purpose !== incoming.purpose ||
        existing.communicationType !== incoming.communicationType ||
        existing.userId !== incoming.userId ||
        existing.externalContactId !== incoming.externalContactId ||
        existing.queueName !== incoming.queueName ||
        !_.isEqual(existing.addresses, incoming.addresses)
    );
};

const ConversationTimelineView = ViewModel.extend(function (
    conversationDetailsV6Observable,
    conversationObservable
) {
    const self = this;

    const isTimelineImprovementsFTEnabled = featureToggleService.isEnabled(
        FeatureToggle.REC_9720_TIMELINE_IMPROVEMENTS
    );

    self.isDisposed = false;

    self.active = ko.observable(false);
    self.timelineData = new DataSet();
    self.timelineGroups = new DataSet();

    /**
     * WEM-437: Track newly added items that need name resolution.
     * This optimization avoids iterating through all timeline items on every update.
     * Instead, we only resolve names for newly added items that have target identifiers.
     */
    if (
        featureToggleService.isEnabled(FeatureToggle.WEM_437_SCREEN_MONITORING)
    ) {
        // Track items that need name resolution
        self.itemsNeedingNameResolution = new Set();

        // Define event handlers as named functions for proper cleanup
        const onAddNeedingNameResolution = function (_event, properties) {
            properties.items.forEach(function (itemId) {
                const item = self.timelineData.get(itemId);
                if (
                    item &&
                    (item.screenMonitoredUserId ||
                        item.monitorCoachBargeParticipantId)
                ) {
                    self.itemsNeedingNameResolution.add(itemId);
                }
            });
        };

        const onUpdateTargetNameResolved = function (_event, properties) {
            properties.items.forEach(function (itemId) {
                const changes = properties.data.find(function (d) {
                    return d.id === itemId;
                });
                if (changes && changes.targetUserName !== undefined) {
                    self.itemsNeedingNameResolution.delete(itemId);
                }
            });
        };

        // Register event listeners
        self.timelineData.on("add", onAddNeedingNameResolution);
        self.timelineData.on("update", onUpdateTargetNameResolved);

        // Store references for cleanup in onDispose
        self._targetNameResolutionAddListener = onAddNeedingNameResolution;
        self._targetNameResolutionUpdateListener = onUpdateTargetNameResolved;
    }

    // Session state tracking map for managing dynamic timeline updates
    self.sessionStateMap = conversationTimelineUtil.createSessionStateMap();

    // Track current conversation ID to reset session state when switching conversations
    self.currentConversationId = null;

    self.timelineAvailable = self.computed(function () {
        return (
            ko.toJS(conversationDetailsV6Observable()) ||
            (ko.toJS(conversationObservable()) &&
                ko.toJS(conversationObservable()).id)
        );
    });

    // specify options
    self.timelineOptions = {
        width: "100%",
        editable: false, // enable dragging and editing events
        type: "range",
        stack: false,
        showCurrentTime: true,
        multiselect: false,
        locale: ko.i18n.getLocale(),
        orientation: "bottom",
        showTooltips: true,
        tooltip: {
            followMouse: true,
            overflowMethod: "flip",
        },
        ...(isTimelineImprovementsFTEnabled
            ? {
                  groupOrder: (a, b) =>
                      (a.startTime ?? Infinity) - (b.startTime ?? Infinity),
              }
            : {}),
        format: featureToggleService.isEnabled(
            FeatureToggle.PURE_2946_INTERNATIONALIZATION_DATE_TIME
        )
            ? DateTimeHelper.createTimelineFormatFunctions(
                  ko.i18n.getTimezone()
              )
            : {
                  minorLabels: {
                      millisecond: ko.i18n.renderString(
                          "conversationTimeline.dateTimeFormat.milliseconds"
                      ),
                      second: ko.i18n.renderString(
                          "conversationTimeline.dateTimeFormat.seconds"
                      ),
                      minute: ko.i18n.renderString(
                          "conversationTimeline.dateTimeFormat.hoursMinutes"
                      ),
                      hour: ko.i18n.renderString(
                          "conversationTimeline.dateTimeFormat.hoursMinutes"
                      ),
                      weekday: ko.i18n.renderString(
                          "conversationTimeline.dateTimeFormat.weekdayDay"
                      ),
                      day: ko.i18n.renderString(
                          "conversationTimeline.dateTimeFormat.day"
                      ),
                      week: ko.i18n.renderString(
                          "conversationTimeline.dateTimeFormat.week"
                      ),
                      month: ko.i18n.renderString(
                          "conversationTimeline.dateTimeFormat.month"
                      ),
                      year: ko.i18n.renderString(
                          "conversationTimeline.dateTimeFormat.year"
                      ),
                  },
                  majorLabels: {
                      millisecond: ko.i18n.renderString(
                          "conversationTimeline.dateTimeFormat.hoursMinutesSeconds"
                      ),
                      second: ko.i18n.renderString(
                          "conversationTimeline.dateTimeFormat.dayMonthHourMinute"
                      ),
                      minute: ko.i18n.renderString(
                          "conversationTimeline.dateTimeFormat.weekdayDayMonth"
                      ),
                      hour: ko.i18n.renderString(
                          "conversationTimeline.dateTimeFormat.weekdayDayMonth"
                      ),
                      weekday: ko.i18n.renderString(
                          "conversationTimeline.dateTimeFormat.monthYear"
                      ),
                      day: ko.i18n.renderString(
                          "conversationTimeline.dateTimeFormat.monthYear"
                      ),
                      week: ko.i18n.renderString(
                          "conversationTimeline.dateTimeFormat.monthYear"
                      ),
                      month: ko.i18n.renderString(
                          "conversationTimeline.dateTimeFormat.year"
                      ),
                      year: "",
                  },
              },
    };

    self.setTimeline = function (timeline) {
        self.timeline = timeline;

        // When an timeline item is selected manually trigger a click event on the popover div to display segment details
        self.timeline.on("select", function (properties) {
            // The id of the selected item and manually trigger a click event on the popover element to activate it
            const selectedItemId = properties.items[0];
            $("#" + selectedItemId).trigger("click");
        });

        // When the timeline is panned/zoomed, manually trigger a scroll event to hide any visible popups
        self.timeline.on("rangechange", function () {
            // Trigger the scroll event
            $(window).scroll();
        });

        setupActionMenu();
    };

    const setupActionMenu = function () {
        const timeline = self.timeline;
        const zoomDepth = 0.2; // Default zoom depth
        const moveSpeed = 0.2; // Default move speed

        function move(percentage) {
            const range = timeline.getWindow();
            const interval = range.end - range.start;

            timeline.setWindow({
                start: range.start.valueOf() - interval * percentage,
                end: range.end.valueOf() - interval * percentage,
            });
        }

        // attach events to the navigation buttons
        document.getElementById("zoomIn").onclick = function () {
            timeline.zoomIn(zoomDepth);
        };
        document.getElementById("zoomOut").onclick = function () {
            timeline.zoomOut(zoomDepth);
        };
        document.getElementById("moveLeft").onclick = function () {
            move(moveSpeed);
        };
        document.getElementById("moveRight").onclick = function () {
            move(-moveSpeed);
        };
    };

    self.updateLiveSegments = function () {
        if (self.active()) {
            const now = moment();
            self.timelineData.forEach(
                function (item) {
                    item.end = now;
                    const duration = item.end.diff(item.start);
                    item.duration =
                        conversationTimelineUtil.roundMillisecondsToNearestSecond(
                            duration
                        );

                    self.timelineData.update(item);
                },
                {
                    filter: function (item) {
                        return item.active !== undefined && item.active;
                    },
                }
            );
        }
    };

    const createGroupObject = function (
        participant,
        session,
        baseConversation
    ) {
        const groupObj = {};

        groupObj.id = conversationTimelineUtil.getParticipantGroup(
            participant,
            session
        );
        let baseConversationParticipant;
        if (baseConversation && baseConversation.participants) {
            baseConversationParticipant = baseConversation.participants.find(
                (p) => p.id === participant.participantId
            );
        }
        groupObj.name = conversationTimelineUtil.getParticipantName(
            participant,
            baseConversationParticipant
        );

        groupObj.purpose =
            conversationTimelineUtil.getParticipantPurpose(participant);
        groupObj.communicationType = session.mediaType;

        groupObj.addresses = [];
        const similarSessions = _.filter(participant.sessions, {
            mediaType: session.mediaType,
        });
        groupObj.addresses = _.uniq(
            _.map(similarSessions, function (similarSession) {
                if (similarSession.addressSelf) {
                    return similarSession.addressSelf;
                }
                if (similarSession.direction === "inbound") {
                    const inboundAddress =
                        similarSession.ani || similarSession.addressFrom;
                    return ko.i18n.formatPhoneAddress(inboundAddress);
                } else if (similarSession.direction === "outbound") {
                    const outboundAddress =
                        similarSession.sessionDnis ||
                        similarSession.dnis ||
                        similarSession.addressTo;
                    return ko.i18n.formatPhoneAddress(outboundAddress);
                }
            })
        );

        if (participant.userId !== undefined) {
            groupObj.userId = participant.userId;
        }

        if (participant.externalContactId !== undefined) {
            groupObj.externalContactId = participant.externalContactId;
        }

        if (
            participant.queueId !== undefined &&
            participant.name !== undefined
        ) {
            groupObj.queueName = participant.name;
        }

        return groupObj;
    };

    const createDataObject = function (
        groupId,
        segment,
        communicationDirection,
        purpose,
        sessionId,
        session,
        options
    ) {
        const dataObj = {};

        dataObj.start = moment(segment.segmentStart);
        dataObj.startMs = dataObj.start.valueOf();
        dataObj.purpose = purpose;
        dataObj.group = groupId;
        const idPrefix = isTimelineImprovementsFTEnabled ? sessionId : groupId;
        dataObj.id = `${idPrefix}_${dataObj.start.valueOf()}`;
        dataObj.segmentType = segment.segmentType;
        dataObj.disconnectType = segment.disconnectType;

        /**
         * WEM-437: (1) Map generic "monitoring" segmentType to monitoring.voice / monitoring.message.
         * (2) Persist target identifiers for tooltip name resolution (screenMonitoredUserId, monitorCoachBargeParticipantId).
         */
        if (
            featureToggleService.isEnabled(
                FeatureToggle.WEM_437_SCREEN_MONITORING
            )
        ) {
            if (dataObj.segmentType == "monitoring") {
                const group = self.timelineGroups.get(groupId);
                const monitoringSegmentType =
                    group &&
                    MONITORING_SEGMENT_TYPE_OVERRIDES[group.communicationType];

                if (monitoringSegmentType) {
                    dataObj.segmentType = monitoringSegmentType;
                }
            }
            if (session.screenMonitoredUserId) {
                dataObj.screenMonitoredUserId = session.screenMonitoredUserId;
            }
            // Only one of monitor/coach/barge applies per segment; set the matching participant ID.
            if (
                segment.segmentType === "monitoring" &&
                session.monitoredParticipantId
            ) {
                dataObj.monitorCoachBargeParticipantId =
                    session.monitoredParticipantId;
            } else if (
                segment.segmentType === "coaching" &&
                session.coachedParticipantId
            ) {
                dataObj.monitorCoachBargeParticipantId =
                    session.coachedParticipantId;
            } else if (
                segment.segmentType === "barging" &&
                session.bargedParticipantId
            ) {
                dataObj.monitorCoachBargeParticipantId =
                    session.bargedParticipantId;
            }
        }

        // Support showing message type -- sms, line, facebook twitter
        if (options && options.messageType) {
            const isTwitterXEnabled = featureToggleService.isEnabled(
                FeatureToggle.PURE_5797_MESSAGING_TWITTER_PRIVATE_DIRECT_MESSAGES
            );

            const isEngagementSourceToggleEnabled =
                featureToggleService.isEnabled(
                    FeatureToggle.PURE_5534_OPEN_MESSAGING_ENGAGEMENT_SOURCE_TAGGING
                );

            dataObj.messageType =
                isTwitterXEnabled && options.messageType === "twitter"
                    ? "x"
                    : options.messageType;

            if (isEngagementSourceToggleEnabled && options.engagementSource) {
                dataObj.engagementSource = options.engagementSource;
            }
        }

        if (segment.segmentEnd !== undefined) {
            dataObj.end = moment(segment.segmentEnd);
            dataObj.active = false;
        } else {
            dataObj.end = moment();
            dataObj.active = true;
        }
        dataObj.endMs = dataObj.end.valueOf();

        const duration = dataObj.end.diff(dataObj.start);
        dataObj.duration =
            conversationTimelineUtil.roundMillisecondsToNearestSecond(duration);
        dataObj.direction = communicationDirection;

        // Add a second to the end of segments with 0 duration (after duration has been
        // calculated) so that it can be displayed.
        if (segment.segmentStart === segment.segmentEnd) {
            dataObj.end = dataObj.end.add(1, "second");
        }

        dataObj.className = "segment-type-" + segment.segmentType;
        dataObj.format = ko.i18n.renderString(
            "interactionsList.timeFormatSeconds"
        );
        dataObj.formattedStartDate = ko.i18n.formatDate(
            dataObj.start.toDate(),
            dataObj.format,
            null,
            FULL_DATE_MEDIUM_TIME_OPTIONS
        );
        dataObj.formattedEndDate = ko.i18n.formatDate(
            dataObj.end.toDate(),
            dataObj.format,
            null,
            FULL_DATE_MEDIUM_TIME_OPTIONS
        );

        const existingGroup = self.timelineGroups.get(groupId);
        if (
            isTimelineImprovementsFTEnabled &&
            existingGroup &&
            (existingGroup.startTime ?? Infinity) > dataObj.startMs
        ) {
            existingGroup.startTime = dataObj.startMs;
            self.timelineGroups.update(existingGroup);
        }
        return dataObj;
    };

    const cachedUsers = {};
    const getCachedUser = function (userId) {
        if (cachedUsers[userId]) {
            return Promise.resolve(cachedUsers[userId]);
        } else {
            return userService
                .getUser({
                    userId,
                    state: "deleted", // this will get active, inactive, and deleted users
                })
                .then(function (user) {
                    cachedUsers[userId] = user;
                    return user;
                });
        }
    };

    /**
     * WEM-437: Resolve target user names for timeline tooltips.
     * This method resolves user names for screen monitoring, monitor, coach, and barge segments.
     * We prefer names from conversation participants (by participantId or userId) to avoid API calls when already available.
     * - Screen monitoring: resolve by screenMonitoredUserId — find participant by userId for participantName first; else getCachedUser (Users API).
     * - Monitor/coach/barge: find participant by monitorCoachBargeParticipantId; if no participant or no userId, set Guest; if userId, resolveUserName (participant name by userId or getCachedUser).
     * getCachedUser uses in-memory cachedUsers—first request per userId hits the API, later requests (e.g. live 5s refresh) use cache.
     * We only call timelineData.update when the resolved name changes to avoid redundant updates on refresh.
     * Performance optimization: Only process items in itemsNeedingNameResolution set to avoid iterating all timeline items.
     */
    self.resolveTimelineTargetNames = function (conversation) {
        if (
            !featureToggleService.isEnabled(
                FeatureToggle.WEM_437_SCREEN_MONITORING
            )
        ) {
            return;
        }

        const targetGuestLabel = ko.i18n.renderString(
            "conversationTimeline.tooltip.targetGuest"
        );

        const setTargetName = function (item, name) {
            const displayName = name?.trim() || targetGuestLabel;
            if (item.targetUserName !== displayName) {
                item.targetUserName = displayName;
                // Only update timeline data if view is not disposed
                if (!self.isDisposed) {
                    self.timelineData.update(item);
                }
            }
        };

        const findParticipantBy = function (key, value) {
            const participants = ko.unwrap(conversation.participants);
            return (
                participants &&
                _.find(participants, (p) => ko.unwrap(p[key]) === value)
            );
        };

        const resolveUserName = function (item, userId) {
            // Check if participant has a name we can use directly
            const participant = findParticipantBy("userId", userId);
            const participantName =
                participant?.participantName &&
                ko.unwrap(participant.participantName);
            if (participantName) {
                setTargetName(item, participantName);
                return;
            }

            // Otherwise fetch from Users API
            getCachedUser(userId)
                .then((user) => {
                    setTargetName(item, user && ko.unwrap(user.name));
                })
                .catch((err) => {
                    setTargetName(item, null);
                });
        };

        // Only process items that need name resolution
        self.itemsNeedingNameResolution.forEach(function (itemId) {
            const item = self.timelineData.get(itemId);
            if (!item) {
                // Item no longer exists in timeline, remove from tracking
                self.itemsNeedingNameResolution.delete(itemId);
                return;
            }

            // Screen monitoring: resolve by user ID
            if (item.screenMonitoredUserId) {
                resolveUserName(item, item.screenMonitoredUserId);
                return;
            }

            // Monitor/coach/barge: find participant by ID and resolve name
            const participantId = ko.unwrap(
                item.monitorCoachBargeParticipantId
            );
            // Resolve name from participant userId; otherwise show as Guest
            const participant = participantId
                ? findParticipantBy("participantId", participantId)
                : undefined;
            const userId = participant
                ? ko.unwrap(participant.userId)
                : undefined;
            if (userId) {
                resolveUserName(item, userId);
            } else {
                setTargetName(item, null);
            }
        });
    };

    self.computed(function () {
        const conversation = ko.toJS(conversationDetailsV6Observable);
        const baseConversation = ko.toJS(conversationObservable);
        if (conversation) {
            // Clear session state tracking only when switching to a different conversation
            if (self.currentConversationId !== conversation.conversationId) {
                self.sessionStateMap.clear();
                self.currentConversationId = conversation.conversationId;
            }
            const isActive =
                conversationTimelineUtil.isConversationActive(conversation);
            self.active(isActive);

            self.timelineOptions.showCurrentTime = isActive;

            if (conversation.participants) {
                // STEP 1: Preprocess sessions for live updates
                if (isTimelineImprovementsFTEnabled) {
                    _.each(conversation.participants, function (participant) {
                        _.each(participant.sessions, function (session) {
                            conversationTimelineUtil.preprocessSession(
                                participant,
                                session,
                                self.timelineData,
                                self.sessionStateMap
                            );
                        });
                    });
                }

                // STEP 2: Process sessions and create/update timeline objects
                // This step creates the actual timeline groups and data items for visualization
                _.each(conversation.participants, function (participant) {
                    _.each(participant.sessions, function (session) {
                        const groupObj = createGroupObject(
                            participant,
                            session,
                            baseConversation
                        );

                        const existingGroup = self.timelineGroups.get(
                            groupObj.id
                        );
                        if (hasGroupChanged(existingGroup, groupObj)) {
                            self.timelineGroups.update(groupObj);
                        }

                        if (session.segments) {
                            _.each(session.segments, function (segment) {
                                // Support showing message type -- sms, line, facebook twitter
                                const options = {
                                    messageType: session.messageType,
                                    engagementSource: session.engagementSource,
                                };
                                const dataObj = createDataObject(
                                    groupObj.id,
                                    segment,
                                    session.direction,
                                    groupObj.purpose,
                                    session.sessionId,
                                    session,
                                    options
                                );
                                self.timelineData.update(dataObj);
                            });
                        }
                    });
                });

                // Get the distinct list of groups with segments
                const groupsWithSegments = self.timelineData.distinct("group");
                const groupIds = self.timelineGroups.getIds();
                const emptyGroups = _.difference(groupIds, groupsWithSegments);

                self.timelineGroups.remove(emptyGroups);

                // WEM-437: Resolve target user names for timeline tooltips
                self.resolveTimelineTargetNames(conversation);

                self.timelineGroups.forEach(
                    function (group) {
                        getCachedUser(group.userId).then(function (user) {
                            // Only update if name has changed
                            if (user.name && user.name !== group.name) {
                                group.name = user.name;
                                self.timelineGroups.update(group);
                            }
                        });
                    },
                    {
                        filter: function (group) {
                            return (
                                group.userId && group.userImage === undefined
                            );
                        },
                    }
                );

                self.timelineGroups.forEach(
                    function (group) {
                        if (
                            !group.userId &&
                            externalContactService.canViewExternalContact(
                                conversation.divisionIds
                            )
                        ) {
                            externalContactService
                                .getCachedExternalContact(
                                    group.externalContactId
                                )
                                .then(function (externalContact) {
                                    if (
                                        externalContact.firstName &&
                                        externalContact.lastName
                                    ) {
                                        const newName = ko.i18n.renderString({
                                            name: "conversationDetails.externalContact.nameFormat",
                                            data: {
                                                firstName:
                                                    externalContact.firstName,
                                                lastName:
                                                    externalContact.lastName,
                                            },
                                        });
                                        // Only update if name has changed
                                        if (newName !== group.name) {
                                            group.name = newName;
                                            self.timelineGroups.update(group);
                                        }
                                    }
                                })
                                .catch(function (err) {
                                    errorCallbackFactory.createCallback({
                                        namespace:
                                            "playback.externalParticipant",
                                        logError: false, // log in the supplied callback function instead
                                        strategies: {
                                            404: function () {
                                                LOG.info(
                                                    `External contact not found.`
                                                );
                                            },
                                            403: function () {
                                                LOG.info(
                                                    `Your account does not have permission to view the external contact.`
                                                );
                                            },
                                            DEFAULT: function () {
                                                LOG.error(
                                                    `Error fetching external contact details for the timeline group`,
                                                    err
                                                );
                                            },
                                        },
                                    })(err);
                                });
                        }
                    },
                    {
                        filter: function (group) {
                            return group.externalContactId;
                        },
                    }
                );
            }
        }
    });

    /**
     * Cleanup handler to prevent memory leaks from async operations.
     * Sets the disposal flag to prevent pending promises from updating the view after disposal.
     * Unsubscribes from timelineData add/update events for target name resolution.
     */
    self.onDispose = function () {
        self.isDisposed = true;

        // Clean up target name resolution event listeners if they were registered
        if (self._targetNameResolutionAddListener) {
            self.timelineData.off("add", self._targetNameResolutionAddListener);
            self.timelineData.off(
                "update",
                self._targetNameResolutionUpdateListener
            );
            self._targetNameResolutionAddListener = null;
            self._targetNameResolutionUpdateListener = null;
        }
    };
});

export default ConversationTimelineView;
